/**
 * DocumentViewer v1.0 -- Shared markdown viewer and document renderer.
 *
 * v1.0: Markdown + instrucciones (marked.js)
 * v2.0: (futuro) Soporte libros, PDFs
 * v3.0: (futuro) Editor WYSIWYG, diff viewer
 *
 * @module presentation/shared/DocumentViewer
 */

import { marked } from 'marked';

marked.setOptions({ breaks: true, gfm: true });

// Deuda 4: este escape no cambiaba la comilla simple; ahora es el compartido.
import { esc } from './texto.js';

/**
 * Renderiza markdown a HTML.
 * @param {string} text - Texto markdown
 * @returns {string} HTML string
 */
export function renderMarkdown(text) {
  if (!text) return '';
  return marked.parse(text);
}

/**
 * Genera HTML del viewer inline.
 * @param {string} content - Contenido a renderizar
 * @param {object} [opts]
 * @param {string} [opts.format='markdown'] - 'markdown' | 'text'
 * @param {string} [opts.maxHeight] - CSS max-height (ej. '400px')
 * @param {string} [opts.id] - ID para el contenedor
 * @returns {string} HTML string
 */
export function renderDocumentViewer(content, opts = {}) {
  const { format = 'markdown', maxHeight, id } = opts;
  const idAttr = id ? ` id="${esc(id)}"` : '';
  const style = maxHeight ? ` style="max-height:${esc(maxHeight)};overflow-y:auto"` : '';

  let html;
  if (format === 'text') {
    html = `<pre style="white-space:pre-wrap;margin:0">${esc(content || '')}</pre>`;
  } else {
    html = content ? renderMarkdown(content) : '<p class="empty-state">Sin contenido</p>';
  }

  return `<div class="doc-viewer"${idAttr}${style}>${html}</div>`;
}

/**
 * Abre modal con documento renderizado.
 * @param {string} title - Titulo del modal
 * @param {string} content - Contenido markdown/texto
 * @param {object} [opts]
 * @param {string} [opts.format='markdown']
 * @param {Function} [opts.onClose]
 */
export function openDocumentModal(title, content, opts = {}) {
  const { format = 'markdown', onClose } = opts;
  const existing = document.getElementById('doc-viewer-modal');
  if (existing) existing.remove();

  const viewerHtml = renderDocumentViewer(content, { format });

  const modal = document.createElement('div');
  modal.id = 'doc-viewer-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:800px;max-height:85vh;display:flex;flex-direction:column">
      <div class="modal-header">
        <span class="modal-title">${esc(title)}</span>
        <button class="modal-close" id="doc-viewer-close">&#x2715;</button>
      </div>
      <div class="modal-body" style="overflow-y:auto;flex:1">
        ${viewerHtml}
      </div>
      <div class="modal-footer">
        <button class="btn-ghost btn-sm" id="doc-viewer-close2">Cerrar</button>
      </div>
    </div>`;

  document.body.appendChild(modal);

  const close = () => {
    modal.remove();
    if (onClose) onClose();
  };
  document.getElementById('doc-viewer-close').onclick = close;
  document.getElementById('doc-viewer-close2').onclick = close;
  modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
}

/**
 * Construye contenido markdown de una instruccion normalizada.
 * @param {object} inst - Instruccion normalizada (con body, steps, code, title, area, etc.)
 * @returns {string} Markdown text
 */
export function buildInstructionContent(inst) {
  const parts = [];

  // body y steps[0].description suelen ser identicos — evitar duplicar
  const body = inst.body || '';
  const firstStepDesc = inst.steps?.[0]?.description || '';
  const bodyIsFirstStep = body && firstStepDesc && body.trim() === firstStepDesc.trim();

  if (body) parts.push(body);

  if (inst.steps?.length) {
    for (const s of inst.steps) {
      if (!s.description) continue;
      if (bodyIsFirstStep && s === inst.steps[0]) continue;
      parts.push(`### Paso ${s.stepOrder}: ${s.paramName || ''}\n${s.description}`);
    }
  }

  // Agregar variables_schema como bloque de codigo si existe
  const vars = inst.variablesSchemaJson || inst.variables_schema;
  if (vars && typeof vars === 'string' && vars.trim().length > 2) {
    let formatted = vars.trim();
    try { formatted = JSON.stringify(JSON.parse(formatted), null, 2); } catch { /* keep as-is */ }
    parts.push(`### Variables Schema\n\`\`\`json\n${formatted}\n\`\`\``);
  }

  return parts.join('\n\n') || '';
}

/**
 * Genera toolbar edit/preview para un textarea.
 * @param {string} textareaId - ID del textarea
 * @param {string} previewId - ID del div preview
 * @returns {string} HTML string
 */
export function renderEditorToolbar(textareaId, previewId) {
  return `
    <div class="doc-viewer-toolbar">
      <button type="button" class="doc-viewer-tab doc-viewer-tab--active" data-mode="edit" data-textarea="${esc(textareaId)}" data-preview="${esc(previewId)}">Editar</button>
      <button type="button" class="doc-viewer-tab" data-mode="preview" data-textarea="${esc(textareaId)}" data-preview="${esc(previewId)}">Preview</button>
    </div>`;
}

/**
 * Vincula eventos de la toolbar edit/preview.
 * @param {HTMLElement} container - Contenedor padre
 */
export function bindEditorToolbar(container) {
  container.addEventListener('click', (e) => {
    const tab = e.target.closest('.doc-viewer-tab');
    if (!tab) return;

    const mode = tab.dataset.mode;
    const textareaId = tab.dataset.textarea;
    const previewId = tab.dataset.preview;
    const $textarea = document.getElementById(textareaId);
    const $preview = document.getElementById(previewId);
    if (!$textarea || !$preview) return;

    // Update active tab
    tab.closest('.doc-viewer-toolbar').querySelectorAll('.doc-viewer-tab').forEach(t =>
      t.classList.toggle('doc-viewer-tab--active', t === tab)
    );

    if (mode === 'preview') {
      const md = $textarea.value || '';
      $preview.innerHTML = renderDocumentViewer(md, { format: 'markdown' });
      $textarea.hidden = true;
      $preview.hidden = false;
    } else {
      $textarea.hidden = false;
      $preview.hidden = true;
    }
  });
}
